"use client"

/**
 * THE TAB CLAIMS THE ONE STAFF FIRM (STAFF-G5b), live: identity, the tab's
 * scope, the membership directory and the active-firm store fed to the pure
 * `staffFirmClaimOf`.
 *
 * Both staff shell sections (`/cs`, `/super-admin`) are in the staff scope, so
 * their requests claim the tab's staff-scope firm. When identity answered for
 * the staff scope with a firm that is not a staff firm and the person has
 * exactly one, the tab claims it (`claimFirm`: claimed, not chosen) and
 * re-reads identity under the claim.
 *
 * ═══ ABOVE EVERY STAFF GATE, NEVER INSIDE ONE ═══
 *
 * The claim is what turns a staff-scope identity read from the server's
 * default firm (a staffer's brokerage) into the staff firm, and each staff
 * section's gate admits only the staff firm. So the claim cannot sit below a
 * gate: the gate would wait for the claim and the claim for the gate, and a
 * tab opened on `/cs` with no staff claim showed "Switch to Cova Staff" with
 * no way out. `StaffFirmClaimSync` runs it once, in the root layout, on the
 * root client the identity query lives in; the staff shell frame only READS
 * the claim's state (`useStaffFirmClaimState`) to show its busy region.
 */
import { useEffect } from "react"
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query"
import { Option } from "effect"
import { useStore } from "zustand"

import { platformFirmsQuery } from "@/features/platform-shell/firm-switcher"
import { useIdentity } from "@/lib/providers/identity"
import { activeFirmStore } from "@/shared/active-firm"
import { useCurrentPlatformScope } from "@/shared/scope/use-current-platform-scope"

import { staffFirmClaimOf, type StaffFirmClaim } from "../lib/staff-firm-claim"
import { PLATFORM_STAFF_SCOPE } from "../lib/staff-scope"

/** Prefix of `IDENTITY_QUERY_KEY` (`["identity", "current"]`): every scope's identity read. */
const IDENTITY_ROOT_KEY = ["identity"] as const

const NOT_IN_STAFF_SCOPE: StaffFirmClaim = { kind: "pending" }

/** Where the tab stands on claiming its staff firm. A read: it claims nothing. */
export const useStaffFirmClaimState = (): StaffFirmClaim => {
  const { identity, identityScope } = useIdentity()
  const currentScope = useCurrentPlatformScope()
  const directory = useQuery(platformFirmsQuery(identity?.userId ?? null))
  const identityFetching = useIsFetching({ queryKey: IDENTITY_ROOT_KEY }) > 0
  const staffScope = Option.getOrNull(PLATFORM_STAFF_SCOPE)
  const claimedOrgId = useStore(activeFirmStore, (state) => (staffScope === null ? null : (state.firms[staffScope] ?? null)))

  // Only on a staff-scope tab, over an identity read FOR the staff scope.
  const inStaffScope = staffScope !== null && currentScope === staffScope && identityScope === staffScope
  if (!inStaffScope) return NOT_IN_STAFF_SCOPE
  const listed = directory.data?.firms
  // A failed directory read names no staff firm: today's path, never a guess.
  const staffFirmIds =
    directory.isError ? [] : listed === undefined ? null : listed.filter((firm) => firm.scope === staffScope).map((firm) => firm.orgId)
  return staffFirmClaimOf({
    staffFirmIds,
    actingOrgId: identity === null ? undefined : identity.orgId,
    claimedOrgId,
    identityFetching,
  })
}

/** Claims the one staff firm when `staffFirmClaimOf` says so, and re-reads identity under it. */
export const useStaffFirmClaim = (): void => {
  const claim = useStaffFirmClaimState()
  const queryClient = useQueryClient()
  const staffScope = Option.getOrNull(PLATFORM_STAFF_SCOPE)
  const claimOrgId = claim.kind === "claim" ? claim.orgId : null

  useEffect(() => {
    if (staffScope === null || claimOrgId === null) return
    activeFirmStore.getState().claimFirm(staffScope, claimOrgId)
    void queryClient.invalidateQueries({ queryKey: IDENTITY_ROOT_KEY })
  }, [staffScope, claimOrgId, queryClient])
}
